/** Thin JSON client for the platform API. Same-origin; cookies carry the session. */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: { fields?: { path: string; message: string }[]; reason?: string; [k: string]: unknown },
  ) {
    super(message);
  }
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const f of this.details?.fields ?? []) out[f.path] ??= f.message;
    return out;
  }
}

/** The request never reached the server (offline, DNS, server down). */
export class OfflineError extends Error {
  constructor() {
    super("You're offline. Changes are saved on this device and will sync later.");
  }
}

type UnauthorizedHandler = (reason: string | undefined) => void;
let onUnauthorized: UnauthorizedHandler = () => undefined;
export function setUnauthorizedHandler(handler: UnauthorizedHandler) {
  onUnauthorized = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Don't treat 401 as a session loss (sign-in forms). */
  allowUnauthorized?: boolean;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      signal,
      headers: {
        'x-requested-with': 'app',
        ...(body !== undefined && !isForm ? { 'content-type': 'application/json' } : {}),
      },
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new OfflineError();
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const err = (data as { error?: { code: string; message: string; details?: ApiError['details'] } } | undefined)?.error;
    if (res.status === 401 && !options.allowUnauthorized) onUnauthorized(err?.details?.reason);
    if (res.status >= 502 && res.status <= 504 && !err) throw new OfflineError();
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return data as T;
}

const humanize = (key: string) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError || err instanceof OfflineError) return err.message;
  if (err && typeof err === 'object' && 'issues' in err) {
    // Validation (zod): name the field so "Invalid input" is actionable.
    const issue = (err as { issues: { message: string; path?: PropertyKey[] }[] }).issues[0];
    if (!issue) return 'Please check the form.';
    const field = issue.path?.find((p): p is string => typeof p === 'string');
    return field ? `${humanize(field)}: ${issue.message}` : issue.message;
  }
  // Unexpected: keep the detail visible (and in the console) instead of hiding it behind a generic message.
  console.error(err);
  const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
  return `Something went wrong${detail}. Please try again.`;
}
